import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Container,
  Stack,
  Typography,
  LinearProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Skeleton,
  Tabs,
  Tab,
  Grid,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import TokenOutlinedIcon from "@mui/icons-material/TokenOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import MemoryOutlinedIcon from "@mui/icons-material/MemoryOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import AutoAwesomeOutlinedIcon from "@mui/icons-material/AutoAwesomeOutlined";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";

import CustomHeader from "../../components/common/CustomHeader";
import CustomSnackbar from "../../components/common/CustomSnackbar";
import CustomCard from "../../components/common/CustomCard";
import SkeletonLoader from "../../components/common/SkeletonLoader";
import CustomFilter from "../../components/common/CustomFilter";
import { subscriptionApi } from "../../api/subscriptionApi";
import { useAuth } from "../../contexts/AuthContext";

// ─── Threshold config ───────────────────────────────────────────────
const NEAR_EXHAUST_PCT = 80; // >= 80% → near-exhaust warning

// ─── Helper: derive status from used / allowed ──────────────────────
const getPlanRowStatus = (used, allowed) => {
  if (!allowed || allowed === 0) return "ok";
  const pct = (used / allowed) * 100;
  if (pct >= 100) return "exhausted";
  if (pct >= NEAR_EXHAUST_PCT) return "near";
  return "ok";
};

// ─── Status badge chip ──────────────────────────────────────────────
const StatusChip = ({ status }) => {
  const map = {
    exhausted: { label: "Exhausted", color: "error", icon: <ErrorOutlineRoundedIcon sx={{ fontSize: 14 }} /> },
    near: { label: "Near Limit", color: "warning", icon: <WarningAmberRoundedIcon sx={{ fontSize: 14 }} /> },
    ok: { label: "OK", color: "success", icon: <CheckCircleOutlinedIcon sx={{ fontSize: 14 }} /> },
  };
  const { label, color, icon } = map[status] || map.ok;
  return (
    <Chip
      label={label}
      color={color}
      size="small"
      icon={icon}
      variant="outlined"
      sx={{ fontWeight: 600, fontSize: "0.7rem" }}
    />
  );
};

// ─── Plan feature rows config ────────────────────────────────────────
const PLAN_ROWS = [
  {
    label: "Platform Credits",
    icon: <TokenOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />,
    usedKey: "credit_used",
    allowedKey: "credit",
  },
  {
    label: "Recruiter Accounts",
    icon: <PeopleOutlinedIcon sx={{ fontSize: 18, color: "primary.main" }} />,
    usedKey: "created_recruiter_count",
    allowedKey: "allowed_recruiter_count",
  },
];

// ─── Credit Breakdown Config ──────────────────────────────────────────
const CREDIT_BREAKDOWN = [
  { label: "Job Upload", value: 15, icon: <DescriptionOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "CV Upload", value: 15, icon: <MicNoneOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Candidate Matching", value: 15, icon: <HubOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Interview", value: 400, icon: <MicNoneOutlinedIcon sx={{ fontSize: 16 }} /> },
  { label: "Job Generation", value: 20, icon: <AutoAwesomeOutlinedIcon sx={{ fontSize: 16 }} /> },
];

// ─── Plan Usage Table ────────────────────────────────────────────────
const PlanUsageTable = ({ subscribedPlan, tenantUsage }) => {
  const anyExhausted = PLAN_ROWS.some(
    (r) => getPlanRowStatus(tenantUsage?.[r.usedKey] ?? 0, r.usedKey === "credit_used" ? (tenantUsage?.credit_purchased ?? 0) : subscribedPlan[r.allowedKey]) === "exhausted"
  );
  const anyNear = PLAN_ROWS.some((r) => {
    const used = tenantUsage?.[r.usedKey] ?? 0;
    const allowed = r.usedKey === "credit_used" ? (tenantUsage?.credit_purchased ?? 0) : (subscribedPlan[r.allowedKey] ?? 0);
    const pct = allowed > 0 ? (used / allowed) * 100 : 0;
    return pct >= 80;
  });

  return (
    <Stack spacing={2}>
      {/* Credit Breakdown - Single Line */}
      <Box sx={{ bgcolor: "background.paper", p: 1, borderRadius: 1, border: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", gap: 1, overflow: "auto" }}>
        <Typography variant="caption" sx={{ fontWeight: 700, fontSize: "0.7rem", whiteSpace: "nowrap", display: "flex", alignItems: "center", gap: 0.3 }}>
          <TokenOutlinedIcon sx={{ fontSize: 12, color: "primary.main" }} />
          Credit Breakdown:
        </Typography>
        {CREDIT_BREAKDOWN.map((item) => (
          <Box key={item.label} sx={{ display: "flex", alignItems: "center", gap: 0.3, whiteSpace: "nowrap", px: 0.75, py: 0.5, bgcolor: "grey.50", borderRadius: 0.5, border: "1px solid", borderColor: "grey.200" }}>
            <Box sx={{ color: "primary.main", display: "flex", alignItems: "center", fontSize: "0.65rem" }}>
              {item.icon}
            </Box>
            <Typography variant="caption" sx={{ fontWeight: 600, fontSize: "0.65rem" }}>
              {item.label}: {item.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Top-level banners */}
      {anyExhausted && (
        <Alert severity="error" icon={<ErrorOutlineRoundedIcon />} sx={{ fontWeight: 600 }}>
          One or more plan limits are <strong>exhausted</strong>. Some features are disabled.
          Please contact your administrator to upgrade the plan.
        </Alert>
      )}

      <TableContainer sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: "background.default" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700, width: "30%" }}>Resource</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: "20%" }}>
                Allocated
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: "20%" }}>
                Utilized
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, width: "15%" }}>
                Available
              </TableCell>
              <TableCell align="left" sx={{ fontWeight: 700, width: "15%" }}>
                Status
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {PLAN_ROWS.map(({ label, icon, usedKey, allowedKey }) => {
              const isCreditRow = usedKey === "credit_used";
              const used = tenantUsage?.[usedKey] ?? 0;
              const allowed = isCreditRow ? (tenantUsage?.credit_purchased ?? 0) : (subscribedPlan[allowedKey] ?? 0);
              const balance = isCreditRow ? (tenantUsage?.credit_balance ?? 0) : (allowed - used);
              const pct = allowed > 0 ? Math.min((used / allowed) * 100, 100) : 0;

              const isDanger = pct >= 80;
              const isExhausted = pct >= 100;
              const barColor = isExhausted ? "error" : (isDanger ? "warning" : "success");

              const formatValue = (val) => isCreditRow ? `${val}` : val;

              return (
                <TableRow key={usedKey} hover sx={isDanger ? { backgroundColor: isExhausted ? "rgba(244, 67, 54, 0.08)" : "rgba(255, 152, 0, 0.12)" } : {}}>
                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1.5}>
                      {icon}
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {label}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{tenantUsage || !isCreditRow ? formatValue(allowed) : "—"}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 700,
                      }}
                    >
                      {tenantUsage ? formatValue(used) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" sx={{ fontWeight: 700, color: balance < 0 ? "error.main" : "inherit" }}>
                      {tenantUsage ? formatValue(balance) : "—"}
                      {balance < 0 && <WarningAmberRoundedIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: "middle" }} />}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ minWidth: 120 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        color={barColor}
                        sx={{
                          flex: 1,
                          height: 8,
                          borderRadius: 99,
                          bgcolor: "grey.200",
                        }}
                      />
                      <Typography variant="caption" sx={{ minWidth: 34, fontWeight: 600 }}>
                        {pct.toFixed(0)}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>


      {anyNear && !anyExhausted && (
        <Alert severity="warning" sx={{ fontWeight: 600 }}>
          Purchase plan near to finish please upgrade the plan
        </Alert>
      )}

      {!tenantUsage && (
        <Alert severity="info" sx={{ mt: 1 }}>
          ℹ️ No usage recorded yet. Start by creating a recruiter or uploading a JD!
        </Alert>
      )}
    </Stack>
  );
};

// ─── Token Usage Filter Config ───
const tokenFilterConfig = [
  {
    key: "used_at",
    label: "Date Range",
    type: "date_range",
  }
];

// ─── Token Usage Table ───────────────────────────────────────────────
// ─── Credit Usage Table ───────────────────────────────────────────────
const CreditUsageTable = ({ tenantUsage }) => {
  if (!tenantUsage) {
    return (
      <Alert severity="info">No credit usage data available yet.</Alert>
    );
  }

  const creditUsed = tenantUsage?.credit_used ?? 0;
  const creditPurchased = tenantUsage?.credit_purchased ?? 0;
  const creditBalance = tenantUsage?.credit_balance ?? 0;
  const creditPercentage = creditPurchased > 0 ? Math.min((creditUsed / creditPurchased) * 100, 100) : 0;

  return (
    <Stack spacing={2}>
      <Box sx={{ bgcolor: "background.paper", p: 2, borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Total Purchased
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "primary.main" }}>
                {creditPurchased.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Used
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "warning.main" }}>
                {creditUsed.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Balance
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: creditBalance < 0 ? "error.main" : "success.main" }}>
                {creditBalance.toLocaleString()}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ textAlign: "center" }}>
              <Typography variant="caption" sx={{ color: "text.secondary", display: "block" }}>
                Usage
              </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {creditPercentage.toFixed(1)}%
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Box>
        <LinearProgress
          variant="determinate"
          value={creditPercentage}
          color={creditPercentage >= 100 ? "error" : creditPercentage >= 80 ? "warning" : "success"}
          sx={{ height: 8, borderRadius: 4 }}
        />
      </Box>
    </Stack>
  );
};


// ─── Main Page ───────────────────────────────────────────────────────
const RecruiterSettingsPage = () => {
  const theme = useTheme();
  const { plan } = useAuth();

  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tokenLoading, setTokenLoading] = useState(true);

  const [subscribedPlan, setSubscribedPlan] = useState(null);
  const [tenantUsage, setTenantUsage] = useState(null);

  // Base Data from API
  const [baseTokenUsage, setBaseTokenUsage] = useState([]);

  // Filter state
  const [tokenFilters, setTokenFilters] = useState([]);

  // Active Filter state for rendering
  const [activeTokenFilters, setActiveTokenFilters] = useState([]);

  const [tokenSearch, setTokenSearch] = useState("");

  const tokenValues = useMemo(() => {
    const obj = {};
    tokenFilters.forEach(({ property, value, operator }) => {
      if (operator) {
        obj[property] = { value, operator };
      } else {
        obj[property] = value;
      }
    });
    return obj;
  }, [tokenFilters]);

  // Derived filtered data
  const filteredTokens = useMemo(() => {
    let result = baseTokenUsage;
    const dateFilter = activeTokenFilters.find((f) => f.property === "used_at");

    if (dateFilter && dateFilter.value) {
      const start = dateFilter.value.start ? new Date(dateFilter.value.start) : null;
      const end = dateFilter.value.end ? new Date(dateFilter.value.end) : null;

      if (start) start.setHours(0, 0, 0, 0);
      if (end) end.setHours(23, 59, 59, 999);

      result = result.filter(row => {
        if (!row.used_at) return true;
        let rowDate = new Date(row.used_at);
        if (isNaN(rowDate.getTime()) && typeof row.used_at === 'string') {
          const parts = row.used_at.split(' ')[0].split('-');
          if (parts.length === 3) {
            rowDate = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          }
        }
        if (isNaN(rowDate.getTime())) return true;
        if (start && rowDate < start) return false;
        if (end && rowDate > end) return false;
        return true;
      });
    }
    if (tokenSearch) {
      const lowerSearch = tokenSearch.toLowerCase();
      result = result.filter(row =>
        (row.process?.toLowerCase().includes(lowerSearch)) ||
        (row.candidate_email?.toLowerCase().includes(lowerSearch)) ||
        (row.job_id?.toLowerCase().includes(lowerSearch))
      );
    }

    return result;
  }, [baseTokenUsage, activeTokenFilters, tokenSearch]);

  const handleFilterChange = (key, value, mode, operator = "equal") => {
    setTokenFilters((prev) => {
      const filtered = prev.filter((f) => f.property !== key);
      return [
        ...filtered,
        { property: key, value, operator }
      ];
    });
  };

  const handleApplyFilters = () => {
    setActiveTokenFilters(tokenFilters);
  };

  const handleClearFilters = () => {
    setTokenFilters([]);
    setActiveTokenFilters([]);
  };

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });

  const showSnackbar = (message, severity = "info") =>
    setSnackbar({ open: true, message, severity });

  // ─── Fetch plan + usage ────────────────────────────────────────
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        setLoading(true);
        const res = await subscriptionApi.getPlanForRecruiter();
        const { status, message, data } = res || {};

        if (status === 0) {
          showSnackbar("Network error. Please check your connection.", "warning");
          return;
        }
        if (status >= 500) {
          showSnackbar(message || "Server error while fetching plan details.", "error");
          return;
        }
        if (status >= 400) {
          showSnackbar(message || "Failed to fetch plan details.", "error");
          return;
        }
        if (status === 200 && data) {
          setSubscribedPlan(data.subscribed_plan ?? null);
          setTenantUsage(data.tenant_usage ?? null);
        }
      } catch (err) {
        console.error("Error fetching plan details:", err);
        showSnackbar("Something went wrong while loading plan details.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchPlan();
  }, []);

  // ─── Fetch token usage ─────────────────────────────────────────
  useEffect(() => {
    const fetchTokens = async () => {
      if (tabValue !== 1) return; // Lazy load Token Data
      if (baseTokenUsage.length > 0) return;

      try {
        setTokenLoading(true);
        const res = await subscriptionApi.getRecruiterTokenUsage();
        const { status, data } = res || {};
        if (status === 200 && Array.isArray(data)) {
          setBaseTokenUsage(data);
        } else {
          setBaseTokenUsage([]);
        }
      } catch (err) {
        console.error("Error fetching token usage:", err);
      } finally {
        setTokenLoading(false);
      }
    };

    fetchTokens();
  }, [tabValue, baseTokenUsage.length]);

  // Determine current plan display name safely
  const activePlanName = subscribedPlan?.plan_name || plan || "Free Trial";

  // ─── Render ────────────────────────────────────────────────────
  return (
    <Box>
      <CustomHeader
        title="Plan & Usage"
        subtitle="Manage your subscription, billing details, and monitor token usage."
        icon={<CreditCardOutlinedIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />}
        breadcrumbs={[
          { label: "Dashboard", path: "/recruiter/dashboard" },
          { label: "Plan & Usage", path: "/plan" }
        ]}
        customChips={[
          {
            label: `Current: ${activePlanName.toUpperCase()}`,
            color: theme.palette.success.main
          }
        ]}
        buttonText="Upgrade Plan"
        buttonIcon={<ArrowUpwardIcon sx={{ fontSize: 16 }} />}
        onButtonClick={() => {
          // console.log("Navigate to plan upgrade logic goes here");
        }}
      />

      <Container maxWidth="lg" sx={{ mt: 3, pb: 6 }}>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={(e, val) => setTabValue(val)}
            aria-label="recruiter settings tabs"
          >
            <Tab
              icon={<CardMembershipOutlinedIcon />}
              iconPosition="start"
              label="Plan"
              id="tab-0"
              sx={{ minHeight: 48 }}
            />
            <Tab
              icon={<MemoryOutlinedIcon />}
              iconPosition="start"
              label="Credit Usage"
              id="tab-1"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>

        {tabValue === 0 && (
          <Stack spacing={4}>
            {/* ── Plan Usage Section ── */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <SpeedOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Plan Usage
                </Typography>
              </Box>

              {loading ? (
                <SkeletonLoader type="table" rows={4} columns={[1, 2, 3, 4, 5]} />
              ) : subscribedPlan ? (
                <CustomCard sx={{ p: 3 }}>
                  {/* Plan name pill */}
                  <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Current Plan:
                    </Typography>
                    <Chip
                      label={subscribedPlan.plan_name || "Unknown Plan"}
                      color="primary"
                      variant="filled"
                      size="small"
                      sx={{ fontWeight: 700 }}
                    />
                  </Box>
                  <PlanUsageTable
                    subscribedPlan={subscribedPlan}
                    tenantUsage={tenantUsage}
                  />
                </CustomCard>
              ) : (
                <Alert severity="warning">
                  Could not load plan details. Please try refreshing or contact your administrator.
                </Alert>
              )}
            </Box>
          </Stack>
        )}

        {tabValue === 1 && (
          <Stack spacing={4}>
            {/* ── Token Usage Section ── */}
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <HubOutlinedIcon color="primary" />
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  Token Usage Data
                </Typography>
              </Box>

              {tokenLoading ? (
                <Stack spacing={1}>
                  {[...Array(4)].map((_, i) => (
                    <Skeleton key={i} variant="rectangular" height={40} sx={{ borderRadius: 1 }} />
                  ))}
                </Stack>
              ) : (
                <CustomCard sx={{ p: 3 }}>
                  <CreditUsageTable tenantUsage={tenantUsage} />
                </CustomCard>
              )}
            </Box>
          </Stack>
        )}
      </Container>

      <CustomSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      />
    </Box>
  );
};

export default RecruiterSettingsPage;